/**
 * « Est-ce qu'on est sur un ordinateur ? » — la porte du parcours candidat.
 *
 * Décision de Kayla (18-08) : le poste se tient sur PC, donc le test se passe
 * sur PC. Un candidat sur téléphone est renvoyé vers son ordinateur
 * (`EcranOrdinateur`) AVANT la vérif technique — mesurer la connexion du
 * téléphone ne dit rien de la machine sur laquelle il travaillera.
 *
 * Pourquoi pas `@/core/hooks/useIsMobile` : il est FIGÉ au boot, exprès (un
 * utilisateur du cockpit sur vrai mobile reste mobile toute sa session, et on
 * évite le thrash à la rotation). Ici c'est l'inverse qu'il faut — quelqu'un
 * qui élargit sa fenêtre, sort du mode responsive des devtools ou branche un
 * écran doit voir le mur se lever tout de suite, sans recharger. On garde en
 * revanche sa liste d'user-agents, qui est la même question.
 *
 * DEUX RÈGLES, dans cet ordre :
 *   1. user-agent de téléphone → JAMAIS un ordinateur, quelle que soit la
 *      largeur. Un téléphone en paysage est large ; ça reste un téléphone, et
 *      le candidat ne tiendra pas un shift dessus.
 *   2. sinon, la largeur décide, et elle reste réactive.
 */
import { useEffect, useState } from "react";

/**
 * En dessous, l'épreuve n'a plus la place de montrer À LA FOIS le fil, le
 * composeur et la consigne — or c'est justement ce que le candidat aura sous
 * les yeux au poste. Une tablette en paysage passe : elle affiche la vraie
 * mise en page, donc elle prouve ce qu'on cherche à prouver.
 */
export const LARGEUR_MINIMUM_PX = 900;

const UA_TELEPHONE =
  /android|iphone|ipod|iemobile|blackberry|opera mini|opera mobi|webos|windows phone|mobile safari/i;

/** `true` si l'appareil se déclare téléphone — indépendant de la largeur. */
export function estUnTelephone(): boolean {
  if (typeof window === "undefined") return false;
  return UA_TELEPHONE.test(window.navigator?.userAgent ?? "");
}

function mesurer(): boolean {
  if (typeof window === "undefined") return true; // SSR : on ne bloque pas.
  if (estUnTelephone()) return false;
  return window.innerWidth >= LARGEUR_MINIMUM_PX;
}

export function useEstOrdinateur(): boolean {
  const [ordinateur, setOrdinateur] = useState(mesurer);

  useEffect(() => {
    const relire = () => setOrdinateur(mesurer());
    // `resize` couvre l'élargissement de fenêtre ET la sortie du mode
    // responsive ; `orientationchange` couvre la tablette qu'on tourne.
    window.addEventListener("resize", relire);
    window.addEventListener("orientationchange", relire);
    relire();
    return () => {
      window.removeEventListener("resize", relire);
      window.removeEventListener("orientationchange", relire);
    };
  }, []);

  return ordinateur;
}
